import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, ICONS, errMsg, timeAgo } from './extUi';
import './tournaments.css';

export default function ExtTournaments() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [name, setName] = useState('');
  const [vetoFormat, setVetoFormat] = useState('vrs');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/v1/ext/tournaments', { params: showArchived ? { archived: 1 } : {} });
      setList(r.data.data || []);
      setError('');
    } catch (e) { setError(errMsg(e, t('ext.loadFailed', 'Failed to load'))); }
    finally { setLoading(false); }
  }, [showArchived, t]);

  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true); setError('');
    try {
      const r = await axios.post('/api/v1/ext/tournaments', { name: name.trim(), veto_format: vetoFormat });
      navigate(`/admin/tournaments/${r.data.id}`);
    } catch (err) { setError(errMsg(err, t('ext.createFailed', 'Failed to create'))); }
    finally { setCreating(false); }
  };

  return (
    <div className="xt-page">
      <header>
        <h1 className="xt-title">{t('ext.title', 'Tournaments')}</h1>
        <p className="xt-lead">
          {t('ext.subtitle', 'External tournaments run on another platform. FluxBot only receives their game-server logs (one log address per tournament), splits them per match and serves live data through the API.')}
        </p>
      </header>

      <section className="xt-sec">
        <div className="xt-sec-head"><h2>{t('ext.newTournament', 'New tournament')}</h2></div>
        <p className="xt-hint">{t('ext.newTournamentHint', 'Creating a tournament gives it its own log address and veto link. Every game server of that tournament sends its logs there.')}</p>
        <form onSubmit={create} className="xt-form">
          <input className="xt-input wide" value={name} onChange={e => setName(e.target.value)} maxLength={120}
            aria-label={t('ext.tournamentNamePlaceholder', 'Tournament name')} placeholder={t('ext.tournamentNamePlaceholder', 'Tournament name')} />
          <select className="xt-input" value={vetoFormat} onChange={e => setVetoFormat(e.target.value)} aria-label={t('ext.vetoFormat', 'Veto format')} title={t('ext.vetoFormat', 'Veto format')}>
            <option value="vrs">{t('ext.vetoFormatVrs', 'VRS veto')}</option>
            <option value="faceit">{t('ext.vetoFormatFaceit', 'FACEIT veto')}</option>
          </select>
          <button type="submit" className="xt-btn primary" disabled={creating || !name.trim()}>
            <Icon d={ICONS.plus} /> {creating ? t('common.loading', 'Loading...') : t('ext.create', 'Create')}
          </button>
        </form>
        {error && <div className="xt-msg bad">{error}</div>}
      </section>

      <section className="xt-sec">
        <div className="xt-sec-head">
          <h2>{showArchived ? t('ext.archivedTournaments', 'Archived tournaments') : t('ext.yourTournaments', 'Your tournaments')}</h2>
          {!loading && <span className="xt-count">{list.length}</span>}
          <span className="grow" />
          <label className="xt-check">
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} /> {t('ext.showArchived', 'Show archived')}
          </label>
        </div>
        {loading ? <div className="xt-empty">{t('common.loading', 'Loading...')}</div>
          : list.length === 0 ? <div className="xt-empty">{t('ext.noTournaments', 'No tournaments yet.')}</div>
          : (
            <div className="xt-list">
              {list.map(tn => {
                const live = tn.stats.last_log_at && Date.now() - new Date(tn.stats.last_log_at).getTime() < 30000;
                return (
                  <Link key={tn.id} to={`/admin/tournaments/${tn.id}`} className="xt-row">
                    <div className="xt-row-top">
                      <div className="xt-row-main">
                        <div className="xt-row-name">{tn.name}</div>
                        <div className="xt-meta">
                          <span>{t('ext.sourcesCount', 'Servers: {{count}}', { count: tn.stats.sources })}</span>
                          <span>{t('ext.lastLog', 'last log {{when}}', { when: tn.stats.last_log_at ? timeAgo(tn.stats.last_log_at) : '—' })}</span>
                        </div>
                      </div>
                      {live && <span className="xt-state ok">{t('ext.receiving', 'Receiving logs')}</span>}
                      {tn.archived && <span className="xt-state off">{t('ext.archived', 'Archived')}</span>}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
      </section>
    </div>
  );
}
